import { getBasicAuthHeader, storeTokens, getStoredTokens } from '../utils/tokenUtils';
import { API_URL } from '../../config/env';

export class AuthExpiredError extends Error {
  constructor(message = 'Session expired') {
    super(message);
    this.name = 'AuthExpiredError';
  }
}

/** Thrown when fetch fails before a response (offline, DNS, CORS, etc.). Callers may retry. */
export class AdminNetworkError extends Error {
  constructor(message = 'Network error', { cause } = {}) {
    super(message);
    this.name = 'AdminNetworkError';
    if (cause !== undefined) this.cause = cause;
  }
}

export const ADMIN_AUTH_EXPIRED_EVENT = 'admin-auth:expired';

let inflightRefresh = null;

// Helper to call refresh endpoint (used outside of React)
async function refreshTokenFlow(refreshToken) {
  const form = new URLSearchParams();
  form.append('grant_type', 'refresh_token');
  form.append('refresh_token', refreshToken);

  const res = await fetch(`${API_URL}/oauth/token`, {
    method: 'POST',
    headers: {
      Authorization: getBasicAuthHeader(),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: form,
  });

  if (!res.ok) throw new Error('Token refresh failed');
  const data = await res.json();
  storeTokens(data.access_token, data.refresh_token);
  window.localStorage.setItem('admin_auth_sync', Date.now().toString());
  return data.access_token;
}

/**
 * Single-flight refresh so parallel 401s share one token rotation.
 */
function refreshWithDedupe(refreshToken) {
  if (!inflightRefresh) {
    inflightRefresh = refreshTokenFlow(refreshToken).finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

function isLikelyNetworkFailure(err) {
  return (
    err instanceof TypeError ||
    err?.name === 'TypeError' ||
    err?.message === 'Failed to fetch'
  );
}

/**
 * fetchWithAuth
 * - Makes a fetch call with the latest Bearer token.
 * - Refreshes token on 401 once, then retries the request.
 * - On refresh failure: dispatches admin-auth:expired (AdminAuthProvider clears session).
 */
export async function fetchWithAuth(url, options = {}) {
  let { accessToken, refreshToken } = getStoredTokens();

  const fetchWithToken = (token) =>
    fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });

  try {
    let res = await fetchWithToken(accessToken);

    if (res.status === 401 && refreshToken) {
      try {
        accessToken = await refreshWithDedupe(refreshToken);
        res = await fetchWithToken(accessToken);
      } catch {
        window.dispatchEvent(new CustomEvent(ADMIN_AUTH_EXPIRED_EVENT));
        throw new AuthExpiredError();
      }
    }

    return res;
  } catch (err) {
    if (err instanceof AuthExpiredError) throw err;
    if (isLikelyNetworkFailure(err)) {
      // Log the message only. Logging the Error keeps it (and its cause and stack) reachable
      // from the console for the life of the tab, which adds up when a request fails on a timer.
      console.error('Network error in fetchWithAuth:', err.message);
      throw new AdminNetworkError('Network error', { cause: err });
    }
    throw err;
  }
}
